/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { Observable } from "@vienna-hypertext/observable";
import { throttle } from "lodash";
import { io, Socket as SocketType } from "socket.io-client";

import {
  AtLeast,
  BlockSnapshot,
  ClientSocketEvents,
  ClientToServerPayloads,
  MouseBeacon,
  NativeSocketEvents,
  ServerSocketEvents,
  ServerToClientPayloads,
  SocketUser,
  Transaction,
  TransactionType,
} from "@vienna/common";

import { BlockFactory } from "src/classes/block/BlockFactory";
import { Point } from "src/classes/plate/lib/types";
import { Plate } from "src/classes/plate/Plate";
import { CurrentUser } from "src/classes/user";
import { Auth } from "src/classes/user/auth/Auth";
import { clientLogger } from "src/lib/logging/datadog";

import { TransactionManager } from "./TransactionManager";
import { PlateSocketSnapshot } from "./types";

export class PlateSocket extends Observable<PlateSocketSnapshot> {
  static readonly RECONNECT_INTERVAL = 1000;

  static readonly INACTIVITY_TIMEOUT = 10000;

  private socket: SocketType<ServerToClientPayloads, ClientToServerPayloads>;

  private readonly plate: Plate;

  private transactionManager: TransactionManager;

  private currentUser: CurrentUser | null;

  constructor(snapshot: PlateSocketSnapshot, plate: Plate) {
    super(snapshot);
    this.plate = plate;
    const currentUser = Auth.instance.user;
    this.currentUser = currentUser;
    this.transactionManager = new TransactionManager(this.plate.snapshot.id, this.currentUser?.snapshot.id || "");

    this.socket = io(`${process.env.API_URL!}/plate`, {
      transports: ["websocket", "polling"], // don't use long polling, just use ws's
      autoConnect: false, // don't connect automatically
      reconnection: false, // don't reconnect automatically
      auth: (cb) => {
        if (typeof window !== "undefined") {
          const token = localStorage.getItem("token");
          cb({ token, avatarUrl: this.currentUser?.snapshot.avatarName, pageId: this.plate.snapshot.id });
        }
      },
      path: "/plate",
    });

    if (this.currentUser) {
      this.connect();
      this.initializeCursorInterval();
      this.initializeListeners();
    }
  }

  /* ------------------ Public Methods ------------------ */

  public createBlocks(blocks: BlockSnapshot[]): void {
    const transaction = this.transactionManager.makeCreateTransaction(blocks);
    this.send(transaction);
  }

  public deleteBlocks(ids: string[]): void {
    const transaction = this.transactionManager.makeDeleteTransaction(ids);
    this.send(transaction);
  }

  public saveBlockUpdate(snapshot: BlockSnapshot, prevSnapshot: BlockSnapshot): void {
    const diff = PlateSocket.difference(snapshot, prevSnapshot);
    if (diff) {
      this.transactionManager.updateRollingTransaction([snapshot]);
      this.throttledSend();
    }
  }

  /**
   * Closes the socket.io connection to the server
   */
  public destroy(): void {
    this.socket.removeAllListeners();
    this.socket.close();
  }

  public sendLockedBlocks(blockIds: string[]): void {
    this.socket.emit(ClientSocketEvents.LockBlocks, { lockedBlocks: blockIds });
  }

  public sendUnlockedBlocks(blockIds: string[]): void {
    this.socket.emit(ClientSocketEvents.UnlockBlocks, { unlockedBlocks: blockIds });
  }

  /**
   * Sends users cursor position to the server
   */
  public sendMouse(position: Point): void {
    this.socket.emit(ClientSocketEvents.MouseBeacon, {
      mousePosition: position,
      username: this.currentUser!.snapshot.username,
    });
  }

  public sendMouseWithThrottle = throttle(
    (position: Point) => {
      if (this.snapshot.presentUsers.length) {
        this.sendMouse(position);
      }
    },
    50,
    { leading: true }
  );

  /**
   * computes selection diff & sends it to socket
   */
  public emitSelectionChange = (newSelection: string[], oldSelection: string[]): void => {
    if (this.snapshot.presentUsers.length) {
      const unlockedBlocks = oldSelection.filter((id) => !newSelection.includes(id));
      const lockedBlocks = newSelection.filter((id) => !oldSelection.includes(id));
      this.sendUnlockedBlocks(unlockedBlocks);
      this.sendLockedBlocks(lockedBlocks);
    }
  };

  /* -------------------------------------------------- */
  /*                       State                        */
  /* -------------------------------------------------- */
  private addPresentUser = (newUser: SocketUser): void => {
    const alreadyHere = this.snapshot.presentUsers.find((u) => u.username === newUser.username);
    if (!alreadyHere) {
      this.setState({ presentUsers: [...this.snapshot.presentUsers, newUser] });
    }
  };

  private removePresentUser = (username: string): void => {
    this.setState({ presentUsers: this.snapshot.presentUsers.filter((u) => u.username !== username) });
    this.unlockBlocksByUsername(username);
    this.removeUsersMouse(username);
  };

  private removeUsersMouse = (username: string): void => {
    const remainingMice = this.snapshot.mice.filter((m) => m.username !== username);
    this.setState({ mice: [...remainingMice] });
  };

  private unlockBlocksByUsername = (username: string): void => {
    this.setState({ lockedBlocks: this.snapshot.lockedBlocks.filter((b) => b.lockedBy !== username) });
  };

  private lockBlock = (id: string, username: string): void => {
    const alreadyLocked = this.snapshot.lockedBlocks.find((b) => b.blockId === id && b.lockedBy === username);
    if (!alreadyLocked) {
      this.setState({ lockedBlocks: [...this.snapshot.lockedBlocks, { blockId: id, lockedBy: username }] });
    }
  };

  private unlockBlock = (id: string, username: string): void => {
    this.setState({
      lockedBlocks: this.snapshot.lockedBlocks.filter((b) => b.blockId !== id && b.lockedBy !== username),
    });
  };

  /* ------------------ Static Methods ------------------ */

  //@todo this is a ChatGPT prompt and a Utility function
  private static difference(
    newSnapshot: BlockSnapshot,
    oldSnapshot: BlockSnapshot
  ): AtLeast<BlockSnapshot, "id"> | null {
    const diffResult = Object.keys(newSnapshot).reduce((diff, key) => {
      if (oldSnapshot[key as keyof BlockSnapshot] === newSnapshot[key as keyof BlockSnapshot]) return diff;
      return {
        ...diff,
        [key as keyof BlockSnapshot]: newSnapshot[key as keyof BlockSnapshot],
        id: oldSnapshot.id,
      };
    }, {}) as AtLeast<BlockSnapshot, "id">;
    return Object.keys(diffResult).length ? diffResult : null;
  }

  /* ------------------ Private Methods ------------------ */

  /**
   * Sends a trasaction to the server
   */
  private send(transaction: Transaction, { isRetry }: { isRetry: boolean } = { isRetry: false }): void {
    transaction.time = new Date();
    if (!isRetry) {
      this.transactionManager.commit(transaction);
      const transactionInfo = this.transactionManager.getInfo(transaction);
      this.setState({ awaitingReceipt: [...this.snapshot.awaitingReceipt, transactionInfo] });
    }
    this.socket.emit(ClientSocketEvents.PlateUpdate, transaction);
  }

  /**
   * Emits update transactions max of every 50ms
   */
  private throttledSend = throttle(
    () => {
      if (this.transactionManager.canSendUpdate()) {
        this.send(this.transactionManager.rollingTransaction);
        this.transactionManager.resetRollingTransaction();
      } else {
        this.throttledSend();
      }
    },
    50,
    { trailing: true }
  );

  /**
   * Resends all committed transactions that haven't been confirmed by the server
   */
  private retryPendingTransactions(): void {
    if (this.transactionManager.ledger.size === 0) return;
    const pendingHashes = Array.from(this.transactionManager.ledger.keys());
    clientLogger.warn(`Retrying pending transactions for user ${this.currentUser!.snapshot.username}`, {
      area: "socket",
      pendingTransactions: pendingHashes,
    });
    this.transactionManager.ledger.forEach((transaction) => {
      this.send(transaction, { isRetry: true });
    });
  }

  /**
   * Hides cursors that are inactive for 10 seconds
   */
  private hideDeadMice(): void {
    const now = new Date();
    const liveMice = this.snapshot.mice.filter(
      (mouse) => +now - +mouse.lastBeacon < PlateSocket.INACTIVITY_TIMEOUT
    );
    this.setState({ mice: liveMice });
  }

  /**
   * Connect socket.io client to server
   */
  private connect(): void {
    if (this.plate.snapshot.editable && !this.snapshot.connected && typeof window !== "undefined") {
      this.socket.connect();
    }
  }

  /**
   * Initialize invterval to remove inactive cursors
   */
  private initializeCursorInterval(): void {
    setInterval(() => {
      if (this.snapshot.presentUsers.length) {
        this.hideDeadMice();
      }
    }, 1000);
  }

  /* ------------------ Socket Event Handlers ------------------ */

  private handleSocketConnect(): void {
    this.setState({ connected: true });
    if (this.plate.snapshot.initialEditors.length) {
      this.socket.emit(ClientSocketEvents.WhoIsHere); // ask api for other users on the Plate
    }
    this.retryPendingTransactions();
  }

  private handleSocketDisconnect(): void {
    this.setState({ connected: true });
    this.socket.connect();
  }

  private handleSocketConnectError(reason: Error): void {
    setTimeout(() => {
      this.socket.connect();
    }, PlateSocket.RECONNECT_INTERVAL);

    clientLogger.error(`Error connecting to socket for user ${this.currentUser!.snapshot.id}`, {
      inner: reason,
    });
  }

  private handleUserJoined(user: SocketUser): void {
    if (user.username === this.currentUser!.snapshot.username) return;
    this.sendLockedBlocks(this.plate.snapshot.selection);
    this.addPresentUser(user);
  }

  private handleUserLeft(username: string): void {
    this.removePresentUser(username);
  }

  private handlePresentUsersReceived(users: SocketUser[]): void {
    users.forEach((user) => {
      this.addPresentUser(user);
    });
  }

  private handleLockedBlocksAdded(blockIds: string[], username: string): void {
    blockIds.forEach((blockId) => {
      this.lockBlock(blockId, username);
    });
  }

  private handleLockedBlocksRemoved(blockIds: string[], username: string): void {
    blockIds.forEach((blockId) => {
      this.unlockBlock(blockId, username);
    });
  }

  private handleMouseBeacon(beacon: MouseBeacon): void {
    const newMouse = { ...beacon, lastBeacon: new Date() };
    const otherMice = this.snapshot.mice.filter((m) => m.username !== beacon.username);
    this.setState({ mice: [...otherMice, newMouse] });
  }

  //@todo cleanup
  private handleIncomingTransaction(transaction: Transaction): void {
    switch (transaction.type) {
      case TransactionType.Create:
        const snapshots = transaction.blocks;
        const blocks = snapshots.map((block) => BlockFactory.create(block, this));
        this.plate.createBlocks(blocks, false, false);
        break;
      case TransactionType.Update:
        transaction.blocks.forEach((partialSnapshot) => {
          const block = this.plate.getBlockById(partialSnapshot.id);
          if (!block) {
            clientLogger.error("Block not found for update transaction");
            return;
          }
          block.handleIncomingSocketUpdate(partialSnapshot);
        });
        break;
      case TransactionType.Delete:
        this.plate.deleteBlocks(transaction.blockIds, false, false);
        break;
    }
  }

  private handlePageSavedReceipt(handledChangeHash: string): void {
    this.transactionManager.removeFromLedger(handledChangeHash); // why do we do this?
    // update the transaction info for sync status icon and alert
    const awaitingReceipt = this.snapshot.awaitingReceipt.filter((t) => t.hash !== handledChangeHash);
    this.setState({ awaitingReceipt });
  }

  /* -------------------------------------------------- */
  /*               Socket Event Listeners               */
  /* -------------------------------------------------- */

  private initializeListeners(): void {
    // connection
    this.socket.on(NativeSocketEvents.Connect, this.handleSocketConnect.bind(this));
    this.socket.on(NativeSocketEvents.Disconnect, this.handleSocketDisconnect.bind(this));
    this.socket.on(NativeSocketEvents.ConnectError, this.handleSocketConnectError.bind(this));

    // collab
    this.socket.on(ServerSocketEvents.UserJoined, (user) => this.handleUserJoined(user));
    this.socket.on(ServerSocketEvents.UserLeft, (username) => this.handleUserLeft(username));
    this.socket.on(ServerSocketEvents.MouseBeacon, ({ mousePosition, username }) =>
      this.handleMouseBeacon({ mousePosition, username })
    );
    this.socket.on(ServerSocketEvents.PresentUsers, (users) => this.handlePresentUsersReceived(users));
    this.socket.on(ServerSocketEvents.LockedBlocks, ({ lockedBlocks, username }) => {
      this.handleLockedBlocksAdded(lockedBlocks, username);
    });
    this.socket.on(ServerSocketEvents.UnlockedBlocks, ({ unlockedBlocks, username }) => {
      this.handleLockedBlocksRemoved(unlockedBlocks, username);
    });
    this.socket.on(ServerSocketEvents.PlateUpdate, (transaction) => this.handleIncomingTransaction(transaction));

    // save
    this.socket.on(ServerSocketEvents.PageSavedReceipt, (handledChangeHash) =>
      this.handlePageSavedReceipt(handledChangeHash)
    );
  }

  /* -------------------------------------------------- */
  /*                   Create Method                    */
  /* -------------------------------------------------- */
  public static create(plate: Plate): PlateSocket {
    const initialSnapshot: PlateSocketSnapshot = {
      presentUsers: [],
      lockedBlocks: [],
      mice: [],
      connected: false,
      awaitingReceipt: [],
      awaitingSend: 0,
    };
    return new PlateSocket(initialSnapshot, plate);
  }
}
